import classNames from 'classnames';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';

import { trackAction } from '@ctct/distui-helpers';
import { generateQeId, Icon, Button, ControlGroup, Input, Spinner, Loadable, LoadingIndicator } from '@ctct/ui-react';

import InfoRow from './InfoRow';
import { getCurrencySymbol, formatCurrency as doFormatCurrency, planNumberEnum } from '../../../utils/pricingUtils';
import { MyAccountApi } from '../api';
import AddOnIds from '../constants/addOnIds';
import { useUpgradeContext } from '../UpgradeContext';
import './_upgradeOrderSummary.scss';
import { formatCurrencyIntoIntegerAndDecimal } from '../utils/currencyUtil';

const UpgradeOrderSummary = (props) => {
  const {
    t,
    className,
    'data-qe-id': qeId,
    selectedPaymentPlan,
    discountAppliedCallback,
    allowEditDiscounts,
    showTaxAndTotal,
    allowRemoveAddOn,
  } = props;
  // current page state stored in purchase context.
  const [upgradeContext, dispatchUpgrade] = useUpgradeContext();
  const {
    contactTiers,
    selectedSMSTier,
    upgradeCurrency: currency,
    discountCode: appliedCode,
    discountAmount: appliedDisAmount,
    upgradeFormattedCustomerInfo,
    paymentPlanGroupId,
    currentPlanId,
    checkout,
    addOnRatings,
    allAddOns,
    designRating,
    passedInDiscountCode,
    upgradeInfo,
    upgradeRating,
    upgradeBillingInfo,
    planNameDescriptionOnly,
    nextChargeScheduledDateDisplayString,
    recommendedUpgradePlanPromotion,
    isSingleBill,
  } = upgradeContext;

  const { contactTierMin, contactTierMax, paymentPlanDiscountPeriod, recommendedPlanId } = upgradeInfo || {
    contactTierMin: 0,
    contactTierMax: 0,
  };

  const {
    totalPrice,
    totalTaxPrice: ratingTotalTaxPriceLocal,
    prepayTotalAmountSaved: contextPrepayTotalAmountSaved,
    amountAppliedFromBalance,
    balanceDue,
    amountToCharge,
    ratePlans,
    proratedPrepayDiscountAmount: proratedPrepayDiscountAmountLocal,
    proratedTotalPrice,
    proratedTotalTaxPrice,
    addOnRating,
  } = upgradeRating || {};
  const [ratePlan] = ratePlans || [{}];
  const { planId } = ratePlan || {};
  const isUpgradingToPremium = planId && planId === planNumberEnum.PREMIUM;

  const SMSAddOn = (addOnRating || []).find((addOn) => addOn.id === AddOnIds.CTCT_SMS_ADDON);

  const hasFreeSMSAddon = SMSAddOn?.fullPrice === 0 && SMSAddOn?.listPrice === 0 && SMSAddOn?.monthlyCost === 0;

  const proratedPrepayDiscountAmount = proratedPrepayDiscountAmountLocal || 0;
  const SMS_INTRO_2022 = 'SMS_INTRO_2022';

  const addOnDiscountTotal = addOnRatings
    ? addOnRatings.reduce((accumulator, addOn) => accumulator + addOn.monthlyDiscount * addOn.period, 0)
    : 0;

  const formatCurrency = (amount) => ((amount || amount === 0) && currency ? doFormatCurrency(currency, amount) : '');
  const { promotionData } = upgradeRating || {};
  const [includedPromoName] = promotionData ? Object.keys(promotionData) : [];
  const includedPromo = includedPromoName ? promotionData[includedPromoName] : null;

  const [discountError, setDiscountError] = useState(null);
  const [hasDiscount, setHasDiscount] = useState(
    !!appliedCode || (includedPromo && includedPromo.promotionCode !== SMS_INTRO_2022)
  );
  const [validatingCode, setValidatingCode] = useState(false);
  const [addingDiscount, setAddingDiscount] = useState(false);
  const [discountCode, setDiscountCode] = useState(appliedCode || '');
  const [discountAmount, setDiscountAmount] = useState(appliedDisAmount || 0);
  const [selectedAddDiscounts, setSelectedAddDiscounts] = useState(false);
  const [promoBeingCalculated, setPromoBeingCalculated] = useState(false);
  const showPromotionCode =
    recommendedUpgradePlanPromotion &&
    !recommendedUpgradePlanPromotion.promotionCode &&
    includedPromo &&
    includedPromo.promotionCode;
  const discountLabel = recommendedUpgradePlanPromotion
    ? t('upgrade-order-summary.label.limited-offer')
    : t('upgrade-order-summary.label.discount');
  const promoLabel = showPromotionCode ? includedPromo.promotionCode : discountLabel;
  const { monthlyPrice, totalTaxPrice } = selectedPaymentPlan.rating || upgradeRating || {};

  const { addedCheckoutOffer } = checkout;

  const trackPageName = selectedPaymentPlan.rating ? 'account.choose.paymentplan' : 'account.choose.checkout';

  const nextBillDate = (upgradeBillingInfo?.overview?.next_charge_scheduled || ',').split(',')[0];
  const nextBillAmount = upgradeRating?.monthlyPrice ? formatCurrency(upgradeRating.monthlyPrice) : null;
  const paymentPeriod = upgradeBillingInfo?.overview?.payment_period || 0;
  const promotionalDiscountNum = (upgradeRating?.monthlyPrice || 0) - (includedPromo?.promotionCredit || 0);

  let promotionalDiscount = null;

  if (!Number.isNaN(promotionalDiscountNum)) {
    promotionalDiscount = formatCurrency(promotionalDiscountNum);
  }

  let ratingTotalTaxPrice = ratingTotalTaxPriceLocal;

  if (!Number.isNaN(proratedTotalTaxPrice)) {
    ratingTotalTaxPrice = proratedTotalTaxPrice;
  }

  const addDiscount = async () => {
    setPromoBeingCalculated(true);

    try {
      const useCode = passedInDiscountCode || discountCode;
      const trimmedCode = useCode.trim().toUpperCase();

      setDiscountCode(trimmedCode);

      if (isEmpty(trimmedCode)) {
        throw t('upgrade-order-summary.promo_error');
      }

      // https://ctct.atlassian.net/browse/CPD-7570
      if (paymentPeriod !== 12 && ['UA25', 'UA40'].includes(trimmedCode)) {
        // eslint-disable-next-line no-throw-literal
        throw 'Code only valid for 12-month plan';
      }

      setValidatingCode(true);

      const discountDetails = await MyAccountApi.getPlanRating({
        contactUsed: contactTierMin,
        currentPlanId,
        selectedPlan: { id: recommendedPlanId },
        customer: upgradeFormattedCustomerInfo,
        discount: {
          discountPeriod: paymentPeriod,
        },
        discountCode: trimmedCode,
        getActivePromotionCodes: !allowEditDiscounts,
        newAddOnServices: allAddOns ? allAddOns.filter((ao) => ao.checked).map((addOn) => addOn.id) : [],
        paymentPlanGroupId: paymentPlanGroupId || selectedPaymentPlan.paymentPlanGroupId,
        selectedCurrency: currency,
        smsUsed: selectedSMSTier?.min,
      });
      const promotionDetails = discountDetails.promotionData && discountDetails.promotionData[trimmedCode];

      if (promotionDetails && !promotionDetails.applied) {
        throw promotionDetails.reasonMessage;
      }

      const { promotionCredit: appliedDiscountAmount } = promotionDetails;

      setDiscountAmount(appliedDiscountAmount);
      setHasDiscount(true);
      setValidatingCode(false);

      if (discountAppliedCallback) {
        discountAppliedCallback({
          discountAmount: appliedDiscountAmount,
          discountCode: trimmedCode,
        });
      }

      dispatchUpgrade({ type: 'setUpgradeRating', value: discountDetails });

      const { addOnRating: addOnRatingsAddDisount } = discountDetails || {};

      if (addOnRatingsAddDisount != null && !isEmpty(addOnRatingsAddDisount)) {
        dispatchUpgrade({ type: 'setAddOnRatings', addOnRatings: addOnRatingsAddDisount });
      }

      dispatchUpgrade({ type: 'setDiscountCode', discountCode: trimmedCode });
      trackAction({
        action: `apply discount`,
        pageName: trackPageName,
        location: 'body',
        element: 'button',
      });
    } catch (err) {
      if (typeof err === 'object') {
        setDiscountError(t('upgrade-order-summary.invalid-promo'));
      } else {
        setDiscountError(err);
      }

      setValidatingCode(false);
      setHasDiscount(false);
      setDiscountAmount(0);
    } finally {
      setPromoBeingCalculated(false);
    }
  };

  useEffect(() => {
    if (paymentPeriod && passedInDiscountCode) {
      const applyPassedInDiscountCode = async () => {
        await addDiscount();
        dispatchUpgrade({ type: 'setPassedInDiscountCode', passedInDiscountCode: null });
      };

      applyPassedInDiscountCode();
    }
  }, [paymentPeriod, passedInDiscountCode]);

  useEffect(() => {
    if (includedPromo) {
      const { proratedPromotionCredit: appliedDiscountAmount, promotionCode } = includedPromo;

      if (discountAmount !== appliedDiscountAmount && promotionCode !== SMS_INTRO_2022) {
        setDiscountAmount(appliedDiscountAmount);
        setHasDiscount(true);
      }
    }
  }, [includedPromo]);

  useEffect(() => {
    const smsAddOnArray = allAddOns ? allAddOns.filter((addon) => addon.id === AddOnIds.CTCT_SMS_ADDON) : null;
    const smsAddOn = smsAddOnArray && smsAddOnArray.length ? smsAddOnArray[0] : null;
    const smsNotChecked = smsAddOn ? smsAddOn.checked === false : false;

    if (discountCode && smsNotChecked) {
      // reapply discount to confirm it's not dependent on SMS Add-on being selected
      addDiscount();
    }
  }, [allAddOns]);

  const discountInputClasses = () =>
    classNames('discount-input', discountError ? 'is-invalid' : '', validatingCode ? 'validating' : '', className);

  const discountRowClasses = () =>
    classNames(
      'upgrade-order-summary-section-row',
      'upgrade-order-summary-discount-row',
      hasDiscount ? '' : 'discount-hide',
      className
    );

  const discountErrorClasses = () => classNames('error-msg', discountError ? '' : 'discount-hide', className);

  const getDiscountContent = () => {
    if (!hasDiscount && passedInDiscountCode == null) {
      return (
        <div className="sidebar-accordion-panel-content" data-qe-id={generateQeId(qeId, 'accordion-content')}>
          <ControlGroup full>
            <Input
              invalid={!!discountError}
              autoFocus
              data-qe-id={generateQeId(qeId, 'discount-input')}
              className={discountInputClasses()}
              placeholder={t('upgrade-order-summary.discount-input-placeholder')}
              onKeyPress={(event) => {
                const { key } = event;

                if (key === 'Enter') {
                  addDiscount();
                }
              }}
              onChange={(event) => setDiscountCode(event.target.value)}
            />
            <Button
              text={
                <>
                  <Icon glyph="plusCircle" /> Apply
                </>
              }
              kind="plain"
              className="discount-submit-button"
              data-qe-id={generateQeId(qeId, 'discount-apply-button')}
              onClick={addDiscount}
              disabled={validatingCode}
              busy={validatingCode}
            />
          </ControlGroup>
          <span className={discountErrorClasses()}>{discountError}</span>
        </div>
      );
    }

    return (
      <div className="sidebar-accordion-panel-content" data-qe-id={generateQeId(qeId, 'accordion-content')}>
        <span data-qe-id={generateQeId(qeId, 'only-one-promo-text')}>
          {t('upgrade-order-summary.discount-dropdown-multiple')}
        </span>
      </div>
    );
  };

  const classes = classNames('upgrade-order-summary', className);

  const removeDiscount = async () => {
    setPromoBeingCalculated(true);

    try {
      trackAction({
        action: 'delete discount',
        pageName: trackPageName,
        location: 'body',
        element: 'button',
      });

      // We should re calculate the rating and update order summary.
      const updatedRating = await MyAccountApi.getPlanRating({
        newAddOnServices: allAddOns ? allAddOns.filter((ao) => ao.checked).map((addOn) => addOn.id) : [],
        customer: upgradeFormattedCustomerInfo,
        // selectedPlan: emailPlan,
        selectedPlan: { id: recommendedPlanId },
        selectedCurrency: currency,
        discount: {
          discountPeriod: paymentPeriod,
        },
        contactUsed: contactTiers.split('-')[0],
        smsUsed: selectedSMSTier?.min,
        paymentPlanGroupId: paymentPlanGroupId || selectedPaymentPlan.paymentPlanGroupId,
        currentPlanId,
      });

      dispatchUpgrade({ type: 'setDiscountData', action: { discountCode: null, discountAmount: 0 } });
      dispatchUpgrade({ type: 'setUpgradeRating', value: updatedRating });

      const { addOnRating: addOnRatingsRemoveDiscount } = updatedRating || {};

      if (addOnRatingsRemoveDiscount != null && !isEmpty(addOnRatingsRemoveDiscount)) {
        dispatchUpgrade({ type: 'setAddOnRatings', addOnRatings: addOnRatingsRemoveDiscount });
      }

      setDiscountAmount(0);
      setDiscountCode('');
      setHasDiscount(false);
      setDiscountError(null);

      if (discountAppliedCallback) {
        discountAppliedCallback({
          discountAmount: 0,
          discountCode: null,
        });
      }
    } catch (e) {
      //
    } finally {
      setPromoBeingCalculated(false);
    }
  };

  useEffect(() => {
    if (
      allowEditDiscounts &&
      selectedPaymentPlan &&
      selectedPaymentPlan.paymentPlanGroupId && //  fix for CBU-5693
      monthlyPrice &&
      discountCode
    ) {
      const newAddDiscountEffect = async () => {
        setAddingDiscount(true);
        await addDiscount();
        setAddingDiscount(false);
      };

      newAddDiscountEffect();
    }
  }, [selectedPaymentPlan]);

  const subtotalSum = totalPrice - ratingTotalTaxPrice;
  let subtotalCharge = !Number.isNaN(subtotalSum)
    ? formatCurrency(subtotalSum + discountAmount + proratedPrepayDiscountAmount + 0)
    : '';

  if (!Number.isNaN(proratedTotalPrice)) {
    subtotalCharge = formatCurrency(proratedTotalPrice);
  }

  const fullTotalSum = amountToCharge;

  const totalCurrency = (
    <span className="upgrade-order-summary-price-currency-left" data-qe-id={generateQeId(qeId, 'price-currency')}>
      {getCurrencySymbol(currency)}
    </span>
  );

  const { integerPartPrice, decimalPartPrice } = formatCurrencyIntoIntegerAndDecimal(fullTotalSum);

  const renderProperHeader = () => {
    return (
      <div
        className="upgrade-order-summary-header"
        data-qe-id={generateQeId(qeId, 'header')}
        style={{ marginLeft: 15, fontSize: 16, marginBottom: -5, fontWeight: 600 }}
      >
        {t('upgrade-order-summary.header')}
      </div>
    );
  };

  if (isSingleBill) {
    return (
      <div className="order-summary-error" data-qe-id={qeId}>
        <div className="order-summary-icon-container">
          <Icon glyph="alertCircle" data-qe-id="sidebar-alert-icon" />
        </div>
        <br />
        <span>{t('upgrade-order-summary.single-bill1')}</span>
        <br />
        <br />
        <span>{t('upgrade-order-summary.single-bill2')}</span>
        <br />
        <span>{t('upgrade-order-summary.single-bill3')}</span>
      </div>
    );
  }

  return (
    <div className={classes} data-qe-id={qeId} style={{ boxShadow: 'none' }}>
      <Loadable loading={!upgradeRating} loader={<LoadingIndicator text="" />}>
        {renderProperHeader()}
        <div
          style={{ paddingTop: 12.5 }}
          className="upgrade-order-summary-section-container"
          data-qe-id={generateQeId(qeId, 'contacts-plan')}
        >
          <InfoRow
            label={t('upgrade-order-summary.label.plan-contacts', {
              planNameDescriptionOnly,
              contactTierMin: contactTierMin?.toLocaleString() || 0,
              contactTierMax: contactTierMax?.toLocaleString() || 0,
            }).replace(' - -1', '+')}
            data-qe-id={generateQeId(qeId, 'contacts-tiers')}
            value={subtotalCharge}
            classBase="contacts"
          />
          <InfoRow
            style={{ marginTop: -12 }}
            innerStyle={{ color: 'dimgray', paddingLeft: 20, paddingTop: 0 }}
            label={
              nextChargeScheduledDateDisplayString == null ? (
                <Spinner size="small" />
              ) : (
                t('upgrade-popup.today-through-date', { date: nextChargeScheduledDateDisplayString })
              )
            }
            data-qe-id={generateQeId(qeId, 'todays-date')}
            value={null}
            classBase="date"
          />
          {!Number.isNaN(proratedPrepayDiscountAmount) && proratedPrepayDiscountAmount !== 0 && (
            <InfoRow
              data-qe-id={generateQeId(qeId, 'prorated-prepay-amount')}
              label={t('upgrade-order-summary.label.prorated-prepay-discount')}
              value={`-${formatCurrency(proratedPrepayDiscountAmount)}`}
              classBase="prepay-discount"
            />
          )}
          {allowEditDiscounts && (
            <div>
              {!hasDiscount && !recommendedUpgradePlanPromotion && (
                <div data-qe-id={generateQeId(qeId, 'add-a-discount')}>
                  <Button
                    text={t('upgrade-order-summary.has-discount')}
                    className="btn-has-discount"
                    kind="link"
                    onClick={() => {
                      setSelectedAddDiscounts(!selectedAddDiscounts);
                    }}
                  />
                </div>
              )}
              {(selectedAddDiscounts || hasDiscount) && (
                <>
                  <div className={discountRowClasses()}>
                    <span
                      className={classNames('upgrade-order-summary-discount-label', 'upgrade-order-summary-body-left')}
                      data-qe-id={generateQeId(qeId, 'discount-code')}
                    >
                      {!recommendedUpgradePlanPromotion && (
                        <Icon
                          data-qe-id={generateQeId(qeId, 'clear-discount-code')}
                          className="close-discount"
                          onClick={removeDiscount}
                          glyph="closeCircle"
                        />
                      )}
                      {promoLabel}: {discountCode}
                    </span>
                    <span
                      className={classNames('upgrade-order-summary-discount-value', 'upgrade-order-summary-body-right')}
                      data-qe-id={generateQeId(qeId, 'discount-value')}
                    >
                      {addingDiscount ? <Spinner size="small" /> : <span>-{formatCurrency(discountAmount)}</span>}{' '}
                    </span>
                  </div>
                  {!recommendedUpgradePlanPromotion && (
                    <div className="upgrade-order-summary-section-row promo-code">
                      <div className="sidebar-accordion-panel">{getDiscountContent()}</div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          {isUpgradingToPremium && hasFreeSMSAddon && (
            <InfoRow
              data-qe-id={generateQeId(qeId, 'sms')}
              label={t('upgrade-order-summary.label.sms')}
              value={t('upgrade-order-summary.label.included')}
              classBase="sms"
            />
          )}
          {showTaxAndTotal && (
            <InfoRow
              data-qe-id={generateQeId(qeId, 'tax-amount')}
              label={t('upgrade-order-summary.label.tax')}
              value={formatCurrency(proratedTotalTaxPrice || totalTaxPrice)}
              classBase="tax"
            />
          )}
          {amountAppliedFromBalance > 0 && (
            <InfoRow
              data-qe-id={generateQeId(qeId, 'amount-applied-from-balance')}
              label={t('upgrade-order-summary.label.amount-applied-from-balance')}
              value={`- ${formatCurrency(amountAppliedFromBalance)}`}
            />
          )}
          {balanceDue < 0 && (
            <InfoRow
              data-qe-id={generateQeId(qeId, 'past-due-balance')}
              label={t('upgrade-order-summary.label.amount-past-due-balance')}
              value={formatCurrency(0 - balanceDue)}
            />
          )}
        </div>
        {addedCheckoutOffer && (
          <div className="upgrade-order-summary-section-container" data-qe-id={generateQeId(qeId, 'section-container')}>
            <div className="upgrade-order-summary-section-row">
              <span className="upgrade-order-summary-body-left upgrade-order-summary-checkout-offer">
                {allowRemoveAddOn && (
                  <Icon
                    data-qe-id={generateQeId(qeId, 'clear-checkout-offer')}
                    className="close-discount"
                    onClick={() => {
                      dispatchUpgrade({ type: 'setAddedCheckoutOffer', addedCheckoutOffer: false });
                      dispatchUpgrade({ type: 'setOfferRemovedAlert', offerRemovedAlert: true });
                    }}
                    glyph="closeCircle"
                  />
                )}
                {t('checkout.checkout_offer.title')}
              </span>
              <span className="upgrade-order-summary-body-right">{formatCurrency(designRating)}</span>
            </div>
          </div>
        )}
        {!allowEditDiscounts && (
          <div className="upgrade-order-summary-section-container" data-qe-id={generateQeId(qeId, 'section-container')}>
            <InfoRow
              data-qe-id={generateQeId(qeId, 'static-discount-section')}
              label={t('upgrade-order-summary.label.total_discount')}
              value={`-${formatCurrency(discountAmount + contextPrepayTotalAmountSaved + addOnDiscountTotal)}`}
              classBase="discount-data"
            />
          </div>
        )}
        {showTaxAndTotal && (
          <div
            className="upgrade-order-summary-section-container"
            data-qe-id={generateQeId(qeId, 'section-container')}
            style={{ border: 'none' }}
          >
            <div className={classNames('upgrade-order-summary-section-row', 'upgrade-order-summary-total-row')}>
              <span
                className={classNames('upgrade-order-summary-total-label', 'upgrade-order-summary-body-left')}
                data-qe-id={generateQeId(qeId, 'total')}
              >
                {t('upgrade-order-summary.label.total')}
              </span>
              <span
                className={classNames('upgrade-order-summary-total-value', 'upgrade-order-summary-body-right')}
                data-qe-id={generateQeId(qeId, 'total-value')}
              >
                {upgradeRating && (
                  <Loadable loading={promoBeingCalculated} loader={<LoadingIndicator text="" />}>
                    <div
                      className={classNames('upgrade-order-summary-price-container')}
                      data-qe-id={generateQeId(qeId, 'price-container')}
                    >
                      {promoBeingCalculated || !upgradeRating ? '.' : totalCurrency}
                      <span className="upgrade-order-summary-price" data-qe-id={generateQeId(qeId, 'dollars-price')}>
                        {!promoBeingCalculated && integerPartPrice.toLocaleString()}
                      </span>
                      <span
                        className="upgrade-order-summary-price-decimal"
                        data-qe-id={generateQeId(qeId, 'cents-price')}
                      >
                        {!promoBeingCalculated && decimalPartPrice}
                      </span>
                    </div>
                  </Loadable>
                )}
              </span>
            </div>
            <InfoRow
              data-qe-id={generateQeId(qeId, 'plan-price')}
              label={t('upgrade-order-summary.label.next-months-price', {
                contactTierMin: contactTierMin?.toLocaleString() || 0,
                contactTierMax: contactTierMax?.toLocaleString() || 0,
              }).replace(' - -1', '+')}
              value={recommendedUpgradePlanPromotion ? promotionalDiscount : nextBillAmount}
              classBase="plan-price"
            />

            <InfoRow
              data-qe-id={generateQeId(qeId, 'upgrade-order-summary.label.next-invoice-date')}
              label={t(
                `upgrade-order-summary.label.next-${paymentPlanDiscountPeriod === 1 ? 'billing' : 'invoice'}-date`
              )}
              value={nextBillDate}
              classBase="next-billing-date"
            />
          </div>
        )}
      </Loadable>
    </div>
  );
};

UpgradeOrderSummary.propTypes = {
  className: PropTypes.string,
  'data-qe-id': PropTypes.string.isRequired,
  discountAppliedCallback: PropTypes.func,
  selectedPaymentPlan: PropTypes.shape({
    rating: PropTypes.shape({
      paymentPeriod: PropTypes.number,
      monthlyPrice: PropTypes.number,
      prepayTotalAmountSaved: PropTypes.number,
      totalPrice: PropTypes.number,
    }),
    paymentPlanGroupId: PropTypes.number,
  }),
  allowRemoveAddOn: PropTypes.bool,
  allowEditDiscounts: PropTypes.bool,
  showTaxAndTotal: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

UpgradeOrderSummary.defaultProps = {
  className: '',
  showTaxAndTotal: true,
  allowRemoveAddOn: true,
  discountAppliedCallback: null,
  selectedPaymentPlan: {
    rating: null,
    paymentPlanGroupId: null,
  },
  allowEditDiscounts: true,
};

export default UpgradeOrderSummary;
